/**
 * BundleComponentsSection — "Bundle Components" widget for Item detail.
 *
 * Shows component items when the current item has an active Product Bundle record.
 * Returns null for non-bundle items (no empty state, no skeleton).
 *
 * Create mode: When __bundle_mode is set, shows a static prompt telling the
 * merchant that items can be added after the product is created.
 *
 * Plan 172 Epic 04 additions:
 * - "Allow customers to select variants" toggle (saves to Product Bundle)
 * - Per-component variant dropdown (saves default_variant to child row)
 *
 * Plan: 171-bundle-product-ux, Epic 01 + 03 + 04
 * Plan: 172-bundle-storefront-pdp, Epic 04
 */

import { useState } from "react";
import { Package, Pencil, Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHeader } from "../../../SectionHeader";
import { useBundleConfig, useSaveLinkedAttributes, customizationQueryKeys } from "@/features/customization/api/customizationAdminHooks";
import { useVariantOptions } from "@/features/customization/api/customizationAdminHooks";
import { cn } from "@/lib/utils";
import { customizationAdminApi } from "@/features/customization/api/customizationAdminApi";
import { EditBundleModal } from "./EditBundleModal";
import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";
import { CARD } from "@/config/design-tokens";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import type { BundleChildItem } from "@/features/customization/api/customizationAdminTypes";

export function BundleComponentsSection({ formData }: ExperienceWidgetProps) {
  const itemName = (formData?.name as string) || "";
  const isNew = !itemName || String(itemName).startsWith("new-");
  const isBundleMode = formData?.__bundle_mode === 1;

  // Create mode: show static prompt when bundle toggle is ON
  if (isNew && isBundleMode) {
    return (
      <div>
        <SectionHeader label="Bundle Components" />
        <div className={CARD}>
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              Bundle Components
            </span>
          </div>
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            Add items to this bundle after the product is created.
          </div>
        </div>
      </div>
    );
  }

  // Non-create mode: delegate to the existing bundle config hook
  if (isNew) return null;

  return <BundleComponentsExisting itemName={itemName} />;
}

/**
 * Existing item bundle components — uses useBundleConfig hook.
 * Separated so the hook is only called for existing items.
 */
function BundleComponentsExisting({ itemName }: { itemName: string }) {
  const bundleConfig = useBundleConfig(itemName);
  const [modalOpen, setModalOpen] = useState(false);

  const tenant = useAuthStore((s) => s.currentTenant);
  const queryClient = useQueryClient();
  const bundleConfigKey = customizationQueryKeys.bundleConfig(tenant, itemName);

  // ── Toggle mutation (dedicated endpoint — frappe.client.set_value re-runs
  // Product Bundle validation and 417s on stock-item parents, which all
  // CSIDE bundles are) ──
  const toggleMutation = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: boolean }) => {
      const client = getERPClient();
      return client.call("zaviago_backend.customization_bundle.save_bundle_setting", {
        bundle_name: bundleConfig.data!.bundle_name,
        field,
        value: value ? 1 : 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bundleConfigKey });
      toast.success("Bundle setting updated");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update"),
  });

  // ── Surcharge mode mutation (string Select value; same dedicated endpoint) ──
  const surchargeModeMutation = useMutation({
    mutationFn: async (mode: "Max" | "Sum") => {
      const client = getERPClient();
      return client.call("zaviago_backend.customization_bundle.save_bundle_setting", {
        bundle_name: bundleConfig.data!.bundle_name,
        field: "surcharge_mode",
        value: mode,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bundleConfigKey });
      toast.success("Bundle setting updated");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update"),
  });

  // ── Default variant mutation (saves via dedicated endpoint) ──
  const setDefaultVariantMutation = useMutation({
    mutationFn: async ({ rowName, variantCode }: { rowName: string; variantCode: string }) => {
      return customizationAdminApi.saveDefaultVariant(rowName, variantCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bundleConfigKey });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save default variant"),
  });

  // ── Linked attributes (Plan 198 Epic 04) — hook must be before early return ──
  const linkedAttrsMutation = useSaveLinkedAttributes();

  // UX spec: show nothing while loading (no skeleton, no flash)
  if (bundleConfig.isLoading || !bundleConfig.data?.is_bundle) {
    return null;
  }

  const items = bundleConfig.data.items || [];
  const totalQty = items.reduce((sum, item) => sum + (item.qty || 1), 0);
  const allowVariantSelection = bundleConfig.data.allow_variant_selection ?? false;
  const variantPricing = bundleConfig.data.variant_pricing ?? false;
  const surchargeMode = bundleConfig.data.surcharge_mode ?? "Max";

  const commonAttributes = bundleConfig.data?.common_attributes || [];
  const linkedAttributes = bundleConfig.data?.linked_attributes || [];

  function handleToggleLinkedAttr(attr: string) {
    const current = [...linkedAttributes];
    const idx = current.indexOf(attr);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(attr);
    }
    linkedAttrsMutation.mutate({
      bundleName: bundleConfig.data!.bundle_name!,
      attrs: current,
    });
  }

  return (
    <div>
      <SectionHeader label="Bundle Components" />

      <div className={CARD}>
        {/* Header row with edit button */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Bundle Components
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label="Edit bundle components"
            onClick={() => setModalOpen(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Variant selection toggle */}
        <div className="px-5 pb-3">
          <div className="flex items-center justify-between gap-3">
            <label
              htmlFor="allow-variant-selection"
              className="text-sm text-foreground cursor-pointer select-none"
            >
              Allow customers to select variants
            </label>
            <Switch
              id="allow-variant-selection"
              checked={allowVariantSelection}
              onCheckedChange={(checked) => toggleMutation.mutate({ field: "allow_variant_selection", value: checked })}
              disabled={toggleMutation.isPending}
              aria-label="Allow customers to select variants"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {allowVariantSelection
              ? "Customers choose color/size for each item on the storefront."
              : "Variant choices are locked to what you configure below."}
          </p>
        </div>

        {/* Variant pricing toggle — only when variant selection is ON */}
        {allowVariantSelection && (
          <div className="px-5 pb-3">
            <div className="border-t border-border/40 pt-3">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="variant-pricing"
                  className="text-sm text-foreground cursor-pointer select-none"
                >
                  Variant pricing
                </label>
                <Switch
                  id="variant-pricing"
                  checked={variantPricing}
                  onCheckedChange={(checked) => toggleMutation.mutate({ field: "variant_pricing", value: checked })}
                  disabled={toggleMutation.isPending}
                  aria-label="Enable variant pricing"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {variantPricing
                  ? "Price adjusts when customers pick higher-priced variants (e.g., XL surcharge)."
                  : "All variant choices use the same bundle price."}
              </p>
            </div>
          </div>
        )}

        {/* Surcharge mode — only when variant pricing is ON */}
        {allowVariantSelection && variantPricing && (
          <div className="px-5 pb-3">
            <div className="border-t border-border/40 pt-3">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="surcharge-mode"
                  className="text-sm text-foreground select-none"
                >
                  Upgrade pricing
                </label>
                <Select
                  value={surchargeMode}
                  onValueChange={(v) => surchargeModeMutation.mutate(v as "Max" | "Sum")}
                  disabled={surchargeModeMutation.isPending}
                >
                  <SelectTrigger id="surcharge-mode" className="h-8 w-[210px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Max">Charge once per set</SelectItem>
                    <SelectItem value="Sum">Charge for every item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {surchargeMode === "Max"
                  ? "One upgrade fee per set — e.g. XL shirt and XL pants add the XL fee once."
                  : "Each higher-priced choice adds its own fee — e.g. XL shirt + XL pants add it twice."}
              </p>
            </div>
          </div>
        )}

        {/* Linked attributes sync — only when variant selection ON + common attrs exist */}
        {allowVariantSelection && commonAttributes.length > 0 && (
          <div className="px-5 pb-3">
            <div className="border-t border-border/40 pt-3">
              <label className="text-sm text-foreground">
                Sync attributes across all items
              </label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                When a customer picks a value, all items in the bundle will match.
              </p>
              <div className="flex flex-wrap gap-2">
                {commonAttributes.map((attr) => (
                  <button
                    key={attr}
                    type="button"
                    onClick={() => handleToggleLinkedAttr(attr)}
                    disabled={linkedAttrsMutation.isPending}
                    className={cn(
                      "rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
                      linkedAttributes.includes(attr)
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-background text-foreground hover:border-foreground/40"
                    )}
                  >
                    {attr}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Component rows */}
        {items.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">
            No items in this bundle yet.
          </div>
        ) : (
          <div>
            {items.map((item, idx) => (
              <BundleComponentRow
                key={item.row_name || item.item_code}
                item={item}
                showBorder={idx > 0}
                allowVariantSelection={allowVariantSelection}
                onDefaultVariantChange={(rowName, variantCode) =>
                  setDefaultVariantMutation.mutate({ rowName, variantCode })
                }
                isSavingVariant={setDefaultVariantMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Footer: total count */}
        {items.length > 0 && (
          <>
            <div className="mx-5 border-t border-border/40" />
            <div className="px-5 py-3">
              <span className="text-xs text-muted-foreground">
                {totalQty} item{totalQty !== 1 ? "s" : ""} total
              </span>
            </div>
          </>
        )}
      </div>

      {/* Lazy-render: only mount modal when open (avoids premature queries) */}
      {modalOpen && bundleConfig.data?.bundle_name && (
        <EditBundleModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          bundleName={bundleConfig.data.bundle_name}
          itemCode={itemName}
          initialItems={items}
        />
      )}
    </div>
  );
}

// ─── Component Row ──────────────────────────────────────────

function BundleComponentRow({
  item,
  showBorder,
  allowVariantSelection,
  onDefaultVariantChange,
  isSavingVariant,
}: {
  item: BundleChildItem;
  showBorder: boolean;
  allowVariantSelection: boolean;
  onDefaultVariantChange: (rowName: string, variantCode: string) => void;
  isSavingVariant: boolean;
}) {
  const qty = item.qty || 1;
  const showVariantDropdown = !allowVariantSelection && item.has_variants;

  return (
    <div
      className={`px-5 py-2.5 ${showBorder ? "border-t border-border/40" : ""}`}
    >
      <div className="flex items-center gap-3">
        {/* Thumbnail */}
        {item.image ? (
          <img
            src={item.image}
            alt={item.item_name}
            className="h-8 w-8 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
            <Package className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        {/* Name + code */}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{item.item_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {item.item_code}
          </div>
        </div>

        {/* Quantity */}
        <span className="text-sm font-medium shrink-0">×{qty}</span>
      </div>

      {/* Variant dropdown — only for template items in pre-defined mode */}
      {showVariantDropdown && (
        <div className="ml-11 mt-1.5">
          <VariantDropdown
            templateItemCode={item.item_code}
            selectedVariant={item.default_variant || ""}
            rowName={item.row_name}
            onChange={onDefaultVariantChange}
            disabled={isSavingVariant}
          />
        </div>
      )}
    </div>
  );
}

// ─── Variant Dropdown ───────────────────────────────────────

function VariantDropdown({
  templateItemCode,
  selectedVariant,
  rowName,
  onChange,
  disabled,
}: {
  templateItemCode: string;
  selectedVariant: string;
  rowName: string;
  onChange: (rowName: string, variantCode: string) => void;
  disabled: boolean;
}) {
  const { data: options, isLoading } = useVariantOptions(templateItemCode);

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading variants...
      </div>
    );
  }

  if (!options || options.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-1">
        No variants available
      </div>
    );
  }

  return (
    <Select
      value={selectedVariant || "__none__"}
      onValueChange={(val) => onChange(rowName, val === "__none__" ? "" : val)}
      disabled={disabled}
    >
      <SelectTrigger className="h-8 text-xs w-full max-w-[280px]">
        <SelectValue placeholder="Select default variant..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__" className="text-xs text-muted-foreground">
          No default
        </SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.item_code} value={opt.item_code} className="text-xs">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
